import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import '../../admin.css';
import { AccountView, AdminUser, adminLogout, adminMe, stopMasqueradeApi } from '../../api/admin';
import { listForms } from '../../api/forms';
import TrialBanner from './TrialBanner';
import BannerStrip from './BannerStrip';
import { ChangePasswordModal, setKnownMinPassword } from '../../components/PasswordDialogs';
import { useToast } from '../../components/Toast';
import { RowMenu } from '../../components/fs';
import { CommandPalette, SearchGlyph, initialsOf, useShellShortcuts } from '../../components/CommandPalette';
import { formCommands, shellCommands } from '../../shared/commandIndex';
import { NARROW, useMediaQuery } from '../../shared/media';
import { useSessionExpiry } from '../../shared/session';
import { accountSettingsHome } from '../../shared/settingsNav';

/**
 * The customer-facing shell: one account's forms, users and settings.
 *
 * R1 (docs/PLAYBOOK-redesign.md §7): a white 52px top bar — brand, the
 * account pill (amber in support mode), Forms · Settings · Platform console,
 * the ⌘K box and the avatar menu (Profile & security · Account settings ·
 * Platform console · Change password · Log out). Under 900px the bar keeps
 * the brand, the pill, a search icon and ☰, and the drawer carries everything
 * the bar and the avatar menu carry. Log out also answers to Ctrl/⌘+Shift+L.
 */
export default function AdminLayout() {
  const [admin, setAdmin] = useState<AdminUser | null>(null);
  const [account, setAccount] = useState<AccountView | null>(null);
  const [masquerading, setMasquerading] = useState(false);
  /** How this support visit went in (074): read-only visits save nothing, and the banner says so. */
  const [supportAccess, setSupportAccess] = useState<'read' | 'full' | null>(null);
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(false);
  const [features, setFeatures] = useState<{ teamAccess?: boolean } | null>(null);
  const [loading, setLoading] = useState(true);
  /**
   * The idle window this session gets, straight from the server.
   *
   * Nobody stays inside this shell without a session, so this is where being
   * thrown out of one is handled: a 401 or a window spent idle ends at the
   * login screen rather than on a Build screen that has quietly stopped
   * saving. 0 until /admin/me answers — see shared/session.ts.
   */
  const [sessionIdleMs, setSessionIdleMs] = useState(0);
  /** Your own password, changeable from wherever you are (rule 6: one modal). */
  const [changing, setChanging] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [avatarOpen, setAvatarOpen] = useState(false);
  const navigate = useNavigate();
  const toast = useToast();
  const location = useLocation();
  useSessionExpiry(sessionIdleMs);
  // Every page under the bar frames itself edge to edge (§1a): a form's
  // workspace draws its own bar and frames each tab (R3, R8 follow-up), the
  // forms list and account settings are rail + content (R8 follow-up, R10).
  // There is no centred column any more (R14 deleted `.ad-body`). Only the
  // workspace takes the 100dvh shell the canvases need.
  const workspace = /\/admin\/forms\/[^/]+(\/|$)/.test(location.pathname);
  const inSettings = location.pathname.startsWith('/admin/settings');
  // Under 900px the bar keeps the brand, the pill, the search icon and a menu
  // button (F2). Nothing of this is rendered on a desktop. The menu closes on
  // route change, Escape and the scrim.
  const narrow = useMediaQuery(NARROW);
  const [menuOpen, setMenuOpen] = useState(false);
  useEffect(() => { setMenuOpen(false); setAvatarOpen(false); }, [location.pathname]);
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setMenuOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [menuOpen]);
  // Something that happened on the way in and has no page of its own to be
  // said on — today, only "your account is ready but checkout would not
  // start". Held in state so it survives this render and no longer.
  const [notice, setNotice] = useState<string>(
    () => (location.state as { notice?: string } | null)?.notice || ''
  );

  /**
   * The folders-and-sharing announcement, shown once per browser.
   *
   * localStorage rather than a stored preference: it is a one-time
   * announcement, it costs nothing if a second device shows it again, and it
   * is not worth a column. Wrapped because a private window or a browser with
   * site data switched off throws on access rather than returning null.
   */
  const [dismissedNews, setDismissedNews] = useState(() => {
    try { return localStorage.getItem('fm.folders.news') === 'seen'; } catch { return false; }
  });
  const dismissFolderNews = () => {
    setDismissedNews(true);
    try { localStorage.setItem('fm.folders.news', 'seen'); } catch { /* nothing to lose */ }
  };

  // The message has been read off the entry; clear it from history so a
  // refresh or a Back does not replay a stale one.
  useEffect(() => {
    if (notice) navigate(location.pathname + location.search, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    adminMe()
      .then((r) => {
        setAdmin(r.admin);
        setAccount(r.account);
        setMasquerading(r.masquerading);
        // An older server has only full access, and says nothing.
        setSupportAccess(r.masquerading ? r.masqueradeAccess ?? 'full' : null);
        setIsPlatformAdmin(r.isPlatformAdmin);
        setFeatures(r.features);
        setSessionIdleMs(r.sessionIdleMs || 0);
        // The minimum every password dialog states (Platform › Rules).
        setKnownMinPassword(r.rules?.minPasswordLength);
        // Platform staff with no account chosen belong in their own console.
        if (r.isPlatformAdmin && !r.account) navigate('/platform', { replace: true });
      })
      .catch(() => navigate('/login', { replace: true }))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onLogout = useCallback(async () => {
    await adminLogout().catch(() => undefined);
    navigate('/login');
  }, [navigate]);

  async function onStopMasquerade() {
    await stopMasqueradeApi().catch(() => undefined);
    navigate('/platform');
  }

  const openPalette = useCallback(() => { setMenuOpen(false); setAvatarOpen(false); setPaletteOpen(true); }, []);
  const closePalette = useCallback(() => setPaletteOpen(false), []);
  useShellShortcuts({ onPalette: openPalette, onLogout });

  const openChangePassword = useCallback(() => { setMenuOpen(false); setAvatarOpen(false); setChanging(true); }, []);
  const commands = useMemo(() => (admin ? shellCommands({
    me: { admin, account, features: features as never, masquerading, isPlatformAdmin },
    pathname: location.pathname,
    onChangePassword: openChangePassword,
    onLogout,
    onExitSupport: masquerading ? onStopMasquerade : undefined,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }) : []), [admin, account, features, masquerading, isPlatformAdmin, location.pathname, openChangePassword, onLogout]);
  const moreCommands = useCallback(
    () => listForms().then((r) => formCommands(r.forms || [], account?.slug)),
    [account?.slug],
  );

  if (loading) return <div className="ad-login"><div className="box">Loading…</div></div>;
  if (!admin) return null;

  // Only to the people who could act on it, and only where folders are theirs
  // to make — a Member cannot create one, so telling them would be noise.
  const showFolderNews = !dismissedNews && !!account && !!features?.teamAccess
    && (isPlatformAdmin || admin.role === 'owner' || admin.role === 'admin');

  const who = admin.name || admin.email;
  const settingsHome = accountSettingsHome({ admin, features: features as never, isPlatformAdmin });
  const roleWord = admin.role ? admin.role[0].toUpperCase() + admin.role.slice(1) : '';
  const avatarHead = <><b>{who}</b><span>{admin.email}{roleWord ? ` · ${roleWord}` : ''}</span></>;

  return (
    <div className={`ad-wrap ${workspace ? 'ad-full' : ''}`}>
      {masquerading && account && (
        <div className="ad-masq">
          <b>Support mode · {supportAccess === 'read' ? 'read-only' : 'full access'}</b>
          <span className="ad-masq-text">
            — you&apos;re viewing <b>{account.name}</b> as Form-Magic staff.{' '}
            {supportAccess === 'read'
              ? 'Nothing you do here can be saved.'
              : 'Changes you make here are real.'}
          </span>
          <button type="button" className="fs-btn sm ad-masq-exit" onClick={onStopMasquerade}>Exit to platform</button>
        </div>
      )}
      <header className="ad-top">
        <Link to="/" className="brand"><span className="ad-mark" aria-hidden="true" />Form-Magic</Link>
        {/* A customer has one account and knows which; only staff visiting one need telling. */}
        {masquerading && account && (
          <span className="ad-acct support" title={`Support mode: ${account.name}`}>
            <span className="ad-acct-mark" aria-hidden="true">{initialsOf(account.name)}</span>
            <span className="ad-acct-name">{account.name}</span>
          </span>
        )}
        {!narrow && (
          <>
            <nav className="ad-nav" aria-label="Primary">
              <NavLink to="/admin" end>Forms</NavLink>
              {/* Users is a settings page now, so Settings stays lit for all of them. */}
              <NavLink to="/admin/settings" className={() => (inSettings ? 'active' : '')}>Settings</NavLink>
              {isPlatformAdmin && <NavLink to="/platform">Platform console ↗</NavLink>}
            </nav>
            <span className="spacer" />
            <button type="button" className="ad-search" onClick={openPalette} aria-label="Search forms, pages and settings (Ctrl or ⌘ K)">
              <SearchGlyph />
              <span className="ad-search-text">Search forms, pages, settings…</span>
              <kbd>⌘K</kbd>
            </button>
            <RowMenu
              open={avatarOpen} onToggle={setAvatarOpen} label={who} head={avatarHead} className="ad-avatar-menu"
              renderTrigger={({ open, onClick, onKeyDown }) => (
                <button
                  type="button" className={`ad-avatar${open ? ' open' : ''}`} aria-label="Account menu"
                  aria-haspopup="menu" aria-expanded={open} onClick={onClick} onKeyDown={onKeyDown}
                >
                  {initialsOf(admin.name, admin.email)}
                </button>
              )}
            >
              <Link to="/admin/settings/profile">Profile &amp; security</Link>
              <Link to={settingsHome}>Account settings</Link>
              {isPlatformAdmin && <Link to="/platform">Platform console</Link>}
              <div className="rule" />
              <button type="button" onClick={openChangePassword}>Change password</button>
              <button type="button" className="danger" onClick={() => void onLogout()}>Log out</button>
            </RowMenu>
          </>
        )}
        {narrow && (
          <>
            <span className="spacer" />
            <button type="button" className="ad-search-icon" aria-label="Search" onClick={openPalette}><SearchGlyph size={20} /></button>
            <button
              type="button" className="ad-menu-btn" aria-label="Menu" aria-expanded={menuOpen}
              onClick={() => setMenuOpen((o) => !o)}
            >
              ☰
            </button>
          </>
        )}
      </header>
      {narrow && menuOpen && (
        <>
          <div className="fs-scrim" onClick={() => setMenuOpen(false)} />
          <div className="fs-drawer ad-menu" role="dialog" aria-modal="true" aria-label="Menu">
            <div className="ad-menu-head">
              <span className="ad-avatar static" aria-hidden="true">{initialsOf(admin.name, admin.email)}</span>
              <span className="who"><b>{who}</b><span>{admin.email}</span></span>
              <button type="button" className="fs-btn icon" title="Close" onClick={() => setMenuOpen(false)}>✕</button>
            </div>
            <nav className="ad-menu-list">
              <NavLink to="/admin" end>Forms</NavLink>
              <NavLink to="/admin/settings" className={() => (inSettings ? 'active' : '')}>Settings</NavLink>
              {isPlatformAdmin && <NavLink to="/platform">Platform console ↗</NavLink>}
              <div className="rule" />
              <Link to="/admin/settings/profile">Profile &amp; security</Link>
              <Link to={settingsHome}>Account settings</Link>
              <button type="button" onClick={openChangePassword}>Change password</button>
              <button type="button" onClick={() => { setMenuOpen(false); void onLogout(); }}>Log out</button>
              {masquerading && account && (
                <button type="button" className="danger" onClick={onStopMasquerade}>Exit to platform</button>
              )}
            </nav>
          </div>
        </>
      )}
      {/* 044's one-time strip. Dismissed for good, per browser: it is an
          announcement, not a setting, and an account that has already made a
          folder has plainly received the news. Deliberately says that NOTHING
          has changed — the whole migration promise is that nobody's morning is
          different until they choose it. */}
      {showFolderNews && (
        <BannerStrip>
          <div className="bl-banner bl-info">
            <span className="bl-banner-pill">New</span>
            <span className="bl-banner-text">
              You can now put forms in folders and choose who can see them.
              Nothing has changed yet — everyone still sees every form.
            </span>
            <div className="bl-banner-actions">
              <Link className="fs-btn sm primary" to="/admin" onClick={dismissFolderNews}>Try it</Link>
              <button className="fs-btn sm neutral" onClick={dismissFolderNews}>Dismiss</button>
            </div>
          </div>
        </BannerStrip>
      )}
      {/* The billing banners — 1e trial states and 1i dunning — sit directly
          under the top bar, above every page heading, on every admin route. */}
      {notice && (
        <BannerStrip>
          <div className="bl-banner bl-amber">
            <span className="bl-banner-pill">Heads up</span>
            <span className="bl-banner-text">{notice}</span>
            <div className="bl-banner-actions">
              <Link className="fs-btn sm primary" to="/admin/settings/billing/plans">Choose your plan</Link>
              <button className="fs-btn sm neutral" onClick={() => setNotice('')}>Dismiss</button>
            </div>
          </div>
        </BannerStrip>
      )}
      {account && <TrialBanner canBill={admin.role === 'owner' || isPlatformAdmin} />}
      <div className="ad-outlet-full"><Outlet /></div>
      <CommandPalette open={paletteOpen} onClose={closePalette} commands={commands} more={moreCommands} />
      {changing && (
        <ChangePasswordModal email={admin.email} onClose={() => setChanging(false)}
          onDone={() => { setChanging(false); toast('Your password has been changed.'); }} />
      )}
    </div>
  );
}
